import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-client';
import { PropertyType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyAddressWebApiHandler_update } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertyConstants from '../../../../constants/PropertyConstants';

const {
  PROPERTY_TYPES: { COMMUNITY, CONDO, HOUSE, MULTIPLEX, TOWNHOUSE },
} = PropertyConstants;

// Community maps to LARGE as the general-purpose community PropertyType
const PROPERTY_TYPE_TO_ENUM: Record<string, PropertyType> = {
  [COMMUNITY]: PropertyType.LARGE,
  [CONDO]: PropertyType.CONDO,
  [MULTIPLEX]: PropertyType.CONDO,
  [HOUSE]: PropertyType.HOUSE,
  [TOWNHOUSE]: PropertyType.TOWNHOUSE,
};

type SavePropertyInfoParams = {
  fullAddress: string;
  unit?: string;
  numUnits?: string;
  propertyId: string;
  propertyType: string;
};

type SavePropertyInfoOptions = {
  onSuccess?: () => void;
  onError?: (error: { code?: string; message?: string }) => void;
};

const useSavePropertyInfo = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateAddress } = useRentalManagerUserPropertyAddressWebApiHandler_update();

  const savePropertyInfo = async (
    { fullAddress, unit, numUnits, propertyId, propertyType }: SavePropertyInfoParams,
    options?: SavePropertyInfoOptions,
  ) => {
    const propertyTypeEnum = PROPERTY_TYPE_TO_ENUM[propertyType];

    try {
      // Step 1: Update address
      const addressResponse = await updateAddress({
        getParams: {
          fullAddress,
          listingAlias: propertyId,
          propertyType: propertyTypeEnum,
          unitNumber: unit,
        },
      });

      // Step 2: Conditionally update numUnits for community properties
      if (propertyType === COMMUNITY && numUnits !== '') {
        await RentalManagerUserPropertyMultiEditWebApiHandler_multi({
          property: {
            listingAlias: propertyId,
            unitsInBuilding: Number(numUnits),
          },
        });
      }

      // Invalidate queries to refresh property data
      queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

      if (options?.onSuccess) {
        options.onSuccess();
      }

      // Return the response
      return addressResponse;
    } catch (error) {
      const errorObject = error as { code?: string; message?: string };

      logError({
        error,
        context: 'useSavePropertyInfo',
      });

      if (options?.onError) {
        options.onError(errorObject);
      }

      throw error;
    }
  };

  return { savePropertyInfo };
};

export { useSavePropertyInfo };
